/**
 * The pieces every desktop widget face shares (mockup 2026-10-26 §1): the
 * face root on the shell's material, the widget type scale, muted text, a
 * rule, a status dot, "+N more" and the face of a frame with nothing to show.
 * Type is on the AppKit scale (a widget has three fixed sizes). Ink comes
 * from the shell (`--widget-*`), falling back to the card's.
 */
import type { CSSProperties, ReactNode } from "react";
import { useT } from "@/i18n";
import { framesSharedMessages } from "@/i18n/messages/frames-shared";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import type { WidgetSize } from "./widget-face";

/** Muted text (mockup `.mu`). */
export const WIDGET_MUTED =
  "text-[color:var(--widget-muted,var(--card-muted))]";

/**
 * The widget type (mockup `.wt`): Denker's display face on the AppKit scale.
 * The 30px figure has no step there, so it is in em of the face's body size,
 * as the card faces size their big figures.
 */
export const WIDGET_DISPLAY = {
  s: "font-display text-appkit-subhead font-medium leading-[1.12] tracking-[-0.02em]",
  m: "font-display text-appkit-title font-medium leading-[1.12] tracking-[-0.02em]",
  l: "font-display text-[2.3em] font-medium leading-none tracking-[-0.035em]",
  xl: "font-display text-appkit-display font-medium leading-none tracking-[-0.04em]",
} as const;

/** A small caps line (mockup `.cap`). */
export const WIDGET_CAPTION =
  "text-appkit-small font-semibold uppercase tracking-[0.02em]";

/**
 * A face with its own paint (Weather, Flight): the card's `--gadget-*`
 * paint tints the material, `name` names it (`data-widget-paint`).
 */
export interface WidgetPaint {
  name: string;
  background: string;
}

/** On a paint the ink is the gadget's white, whatever the material. */
const PAINTED_INK = {
  "--widget-ink": "var(--gadget-ink)",
  "--widget-muted": "var(--gadget-ink-soft)",
  "--widget-line": "var(--gadget-track)",
  "--widget-fill": "var(--gadget-track)",
} as CSSProperties;

/** The paint layer's strength; the shell may set it per material. */
const PAINT_LAYER: CSSProperties = {
  opacity: "var(--widget-paint-opacity, 0.62)",
};

/** A face filling its widget: the paint layer, then the padded column. */
export function WidgetFaceRoot({
  frame,
  size,
  paint,
  className,
  children,
}: {
  frame: FrameData;
  size: WidgetSize;
  paint?: WidgetPaint;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="relative h-full w-full overflow-hidden text-appkit-body leading-[1.35] text-[color:var(--widget-ink,var(--card-ink))]"
      style={paint ? PAINTED_INK : undefined}
      data-testid={`widget-face-${frame.frameType}`}
      data-widget-size={size}
      data-widget-paint={paint?.name}
    >
      {paint && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ ...PAINT_LAYER, background: paint.background }}
        />
      )}
      <div
        className={cn(
          "relative flex h-full min-h-0 min-w-0 flex-col gap-1.5 px-4 py-[15px]",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** A hairline between parts (mockup `.hr`). */
export function WidgetRule({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "h-px shrink-0 bg-[var(--widget-line,var(--card-line))]",
        className,
      )}
    />
  );
}

/** A 7px status dot in `color` (a token). */
export function WidgetDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-[7px] w-[7px] shrink-0 rounded-full"
      style={{ background: color }}
    />
  );
}

/** "+3 more", muted. */
export function WidgetMore({
  count,
  className,
}: {
  count: number;
  className?: string;
}) {
  const t = useT(framesSharedMessages);
  if (count <= 0) return null;
  return (
    <p className={cn("truncate", WIDGET_MUTED, className)}>
      {t("moreCount", { count })}
    </p>
  );
}

/** A frame with nothing to glance at: its title and why. */
export function WidgetEmpty({
  frame,
  size,
  message,
}: {
  frame: FrameData;
  size: WidgetSize;
  message: string;
}) {
  return (
    <WidgetFaceRoot frame={frame} size={size}>
      <p className={cn("line-clamp-2", WIDGET_DISPLAY.s)}>
        {frameDisplayTitle(frame)}
      </p>
      <p className={cn("mt-auto", WIDGET_MUTED)} data-testid="widget-empty">
        {message}
      </p>
    </WidgetFaceRoot>
  );
}
